// Minimal Streamdown stub — CopilotKit's react-core imports `{ Streamdown }`
// as its primary markdown renderer. The real package pulls mermaid, katex,
// shiki, marked + ~20 MB of language/theme assets. For a customer-service
// chat we don't need diagram rendering or LaTeX; lightweight markdown
// (bold/italic/links/lists/code) is enough. This stub does a conservative
// transformation: it preserves whitespace via a CSS class and applies a few
// regex-based replacements for the most-used patterns. Anything fancier
// (tables, syntax highlighting, math, mermaid diagrams) renders as plain
// text — acceptable trade-off for a 4-5 MB gzip reduction.

import { createElement } from "react";
import type { ReactNode } from "react";

interface StreamdownProps {
  children?: ReactNode;
  className?: string;
  // Accept-and-ignore — preserves shape parity with real streamdown so
  // CopilotKit's prop passing doesn't break.
  components?: unknown;
  rehypePlugins?: unknown;
  remarkPlugins?: unknown;
  remarkRehypeOptions?: unknown;
  controls?: unknown;
  parseIncompleteMarkdown?: boolean;
  [key: string]: unknown;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Conservative regex-based markdown → HTML. Covers the patterns the
// supervisor agent actually produces; intentionally NOT a CommonMark
// implementation — anything unrecognised falls through as escaped text.
function renderMarkdown(input: string): string {
  let html = escapeHtml(input);

  // Code fences: ```lang\n...\n``` → <pre><code>...</code></pre>
  html = html.replace(/```(\w+)?\n?([\s\S]*?)```/g, (_m, _lang, code) => {
    return `<pre class="cb-aicsr-codeblock"><code>${code.trim()}</code></pre>`;
  });

  // Inline code: `foo` → <code>foo</code>
  html = html.replace(/`([^`\n]+?)`/g, "<code>$1</code>");

  // Bold: **foo** → <strong>foo</strong>
  html = html.replace(/\*\*([^*]+?)\*\*/g, "<strong>$1</strong>");

  // Italic: *foo* → <em>foo</em>  (avoid double-matching ** already done)
  html = html.replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, "<em>$1</em>");

  // Links: [text](url) → <a href="url">text</a>
  html = html.replace(
    /\[([^\]]+?)\]\(((?:https?:|mailto:|tel:|\/)[^)\s]+?)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  );

  // Headings: # Foo, ## Foo, ### Foo (line-anchored)
  html = html.replace(/^### (.+)$/gm, "<h3>$1</h3>");
  html = html.replace(/^## (.+)$/gm, "<h2>$1</h2>");
  html = html.replace(/^# (.+)$/gm, "<h1>$1</h1>");

  // Unordered list items: "- foo" or "* foo" → bullet line
  html = html.replace(/^[-*] (.+)$/gm, "<li>$1</li>");

  // Wrap consecutive <li>...</li> in <ul>
  html = html.replace(/(<li>[\s\S]+?<\/li>)(?!\s*<li>)/g, "<ul>$1</ul>");

  // Two-or-more newlines → paragraph break; single newline → <br>
  html = html
    .split(/\n{2,}/)
    .map((para) => {
      // Don't wrap if already block-level
      if (/^<(h\d|ul|ol|pre|blockquote|table)/i.test(para.trim())) return para;
      return `<p>${para.replace(/\n/g, "<br>")}</p>`;
    })
    .join("\n");

  return html;
}

export function Streamdown(props: StreamdownProps) {
  const content = typeof props.children === "string" ? props.children : "";
  const html = renderMarkdown(content);
  return createElement("div", {
    className: ["cb-aicsr-markdown", props.className].filter(Boolean).join(" "),
    dangerouslySetInnerHTML: { __html: html },
  });
}

export const parseMarkdownIntoBlocks = (markdown: string): string[] => {
  return markdown.split(/\n{2,}/);
};

export default Streamdown;
